export type MemberRecord = {
  id: string
  userId: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'member'
  joinedAt: string
  // null = unlimited (owner default); a finite number is the member's monthly cap.
  aiCreditAllocation: number | null
  aiCreditsUsedThisPeriod: number
}

export type WorkspaceCreditSummary = {
  monthlyAiCredits: number
  allocated: number
  unallocated: number
  periodResetAt: string
  planName: string | null
  seats: number | null
}

export type LocalUser = { name?: string; email?: string }

export type MemorySettings = {
  workspaceId: string
  workspaceMemoryEnabled: boolean
  userMemoryEnabled: boolean
  semanticRecallEnabled: boolean
}

export type MemorySettingsField = Exclude<keyof MemorySettings, 'workspaceId'>

export type MemorySettingsUpdateInput = Partial<Omit<MemorySettings, 'workspaceId'>>

export type MemoryFactScope = 'workspace' | 'user'

export type MemoryFactCategory = 'preference' | 'account' | 'process' | 'goal' | 'other'

export type MemoryFact = {
  id: string
  scope: MemoryFactScope
  userId: string | null
  content: string
  category: MemoryFactCategory
  sourceConversationId: string
  createdAt: string
  updatedAt: string
}

export type MemoryFactsPage = { facts: MemoryFact[]; nextCursor: string | null }

export type QualificationNotificationMode = 'all' | 'qualified_only' | 'unqualified_only' | 'off'

export type QualificationNotificationSettings = {
  workspaceId: string
  mode: QualificationNotificationMode
}

export type AutomationMode = 'auto' | 'approval' | 'notify' | 'off'

// action keys are plain strings, not a hardcoded literal union — the backend
// (apps/api/src/features/automation/types.ts) is the source of truth for which keys
// exist, so nothing here needs to be kept in sync with it action-by-action.
export type AutomationSettings = {
  workspaceId: string
  actions: Record<string, AutomationMode>
}

export type AutomationActionStatus = {
  lastActivityAt: string | null
  countLast7Days: number
}

export type ApprovalQueueItemStatus = 'pending' | 'approved' | 'dismissed'

export type ApprovalQueueItem = {
  id: string
  workspaceId: string
  userId: string
  trackingId: string
  actionKey: string
  to: string
  originalSubject: string
  draftSubject: string
  draftBody: string
  draftSource: 'ai' | 'fallback_template'
  status: ApprovalQueueItemStatus
  resolvedBy?: string
  resolvedAt?: string
  createdAt: string
  updatedAt: string
}

export const ROLE_ORDER: Record<string, number | undefined> = { owner: 0, admin: 1, member: 2 }
export const roleOrder = (role: string) => ROLE_ORDER[role] ?? 99

export const ROLE_COLORS: Record<string, string> = {
  owner: 'rgba(234,179,8,0.15)',
  admin: 'rgba(79,70,229,0.12)',
  member: 'transparent',
}

export const ROLE_TEXT: Record<string, string> = {
  owner: '#CA8A04',
  admin: '#4F46E5',
  member: '#6B7280',
}
